import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "hiddenInput"]
  static values = { searchUrl: String }

  connect() {
    this.debounceTimer = null
    this.selectedIndex = -1
    this.results = []

    this.boundHideDropdown = this.hideDropdown.bind(this)
    document.addEventListener('click', this.boundHideDropdown)
  }

  disconnect() {
    document.removeEventListener('click', this.boundHideDropdown)
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  search(event) {
    const query = event.target.value.trim()
    
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.navigateDown()
      return
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.navigateUp()
      return
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (this.selectedIndex >= 0) {
        this.selectCurrent()
      }
      return
    } else if (event.key === 'Escape') {
      this.hideDropdown()
      this.inputTarget.blur()
      return
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (query.length < 2) {
      this.hideDropdown()
      this.clearHiddenInput()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.append('q', query)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }
      
      const data = await response.json()
      this.displayResults(data)
      
    } catch (_error) {
      this.showError('Search unavailable')
    }
  }

  displayResults(results) {
    this.results = results
    this.selectedIndex = results.length > 0 ? 0 : -1
    
    const dropdown = this.dropdownTarget
    
    if (results.length === 0) {
      dropdown.innerHTML = '<div class="hollander-empty">No parts found</div>'
    } else {
      dropdown.innerHTML = results.map((result, index) => `
        <div class="hollander-result ${index === this.selectedIndex ? 'selected' : ''}" 
             data-index="${index}" 
             data-hollander="${result.hollander}"
             data-action="click->hollander-search#select">
          <span class="hollander-code">${result.hollander}</span>
          <span class="hollander-description">${result.plain_english_name}</span>
        </div>
      `).join('')
    }
    
    this.showDropdown()
  }

  showError(message) {
    const dropdown = this.dropdownTarget
    dropdown.innerHTML = `<div class="hollander-empty">${message}</div>`
    this.showDropdown()
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (this.results[index]) {
      this.selectResult(this.results[index])
    }
  }

  selectResult(result) {
    this.inputTarget.value = result.plain_english_name
    this.hiddenInputTarget.value = result.hollander
    
    this.hideDropdown()
    
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    
    this.autoSubmitForm()
  }
  
  autoSubmitForm() {
    const form = this.element.closest('form')
    if (form && this.hiddenInputTarget.value) {
      setTimeout(() => {
        form.dispatchEvent(new CustomEvent('hollander:selected', { bubbles: true }))
      }, 100)
    }
  }

  selectCurrent() {
    if (this.selectedIndex >= 0 && this.results[this.selectedIndex]) {
      this.selectResult(this.results[this.selectedIndex])
    }
  }

  navigateDown() {
    if (this.selectedIndex < this.results.length - 1) {
      this.selectedIndex++
      this.updateSelection()
    }
  }

  navigateUp() {
    if (this.selectedIndex > 0) {
      this.selectedIndex--
      this.updateSelection()
    } else if (this.selectedIndex === 0) {
      this.selectedIndex = -1
      this.updateSelection()
    }
  }

  updateSelection() {
    const items = this.dropdownTarget.querySelectorAll('.hollander-result')
    items.forEach((item, index) => {
      item.classList.toggle('selected', index === this.selectedIndex)
    })
    
    const selected = items[this.selectedIndex]
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }

  showDropdown() {
    this.dropdownTarget.style.display = 'block'
  }

  hideDropdown(event) {
    if (event && this.element.contains(event.target)) {
      return
    }
    
    this.dropdownTarget.style.display = 'none'
    this.selectedIndex = -1
  }

  clearHiddenInput() {
    if (this.hiddenInputTarget.value && !this.inputTarget.value) {
      this.hiddenInputTarget.value = ''
    }
  }

  clear() {
    this.inputTarget.value = ''
    this.hiddenInputTarget.value = ''
    this.hideDropdown()
    this.inputTarget.focus()
  }
}